import './Integrations.scss';
import { ReactComponent as AmazonIcon } from '../../icons/amazon-circle.svg';
import EasybillIcon from '../../icons/easybill.svg';
import BillbeeIcon from '../../icons/billbee.svg';
import { AmazonIntegration, EasybillData } from '../../core/types';
import { normalizeDateFE } from '../../core/normalization';
import { useModal } from '../Modal/useModal';
import { useTranslation } from 'react-i18next';
import { ReactComponent as IntegrationsIcon } from '../../icons/integrations.svg';
import { useState } from 'react';
import { ReactComponent as LinkIcon } from '../../icons/link.svg';
import Button from '../Button';
import { formatDate } from '../../core/helpers';
import AmazonTokenModal from '../AmazonTokenModal/AmazonTokenModal';
import EasybillTokenModal from '../EasybillTokenModal/EasybillTokenModal';
import { useFlags } from 'launchdarkly-react-client-sdk';
import BillbeeTokenModal from '../BillbeeTokenModal/BillbeeTokenModal';
import { ReactComponent as TipIcon } from '../../icons/tip-green-circle.svg';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { addNotification } from '../../core/store/notificationsSlice';
import { ReactComponent as ErrorIcon } from '../../icons/alert-red.svg';

interface Props {
  integrations: AmazonIntegration[] | undefined;
  easybillData: EasybillData | undefined;
}

export default function Integrations({ integrations, easybillData }: Props) {
  const activeCompany = useAppSelector((s) => s.user.activeCompany);

  const { vs3776BillbeeToken } = useFlags();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [activeSellerId, setActiveSellerId] = useState<string>();

  const {
    isModalOpened: isAmazonTokenModalOpened,
    closeModal: onCloseAmazonTokenModal,
    openModal: openAmazonTokenModal,
  } = useModal();

  const {
    isModalOpened: isEasybillTokenModalOpened,
    closeModal: closeEasybillTokenModal,
    openModal: openEasybillTokenModal,
  } = useModal();

  const {
    isModalOpened: isBillbeeTokenModalOpened,
    closeModal: closeBillbeeTokenModal,
    openModal: openBillbeeTokenModal,
  } = useModal();

  const closeAmazonTokenModal = () => {
    activeSellerId && setActiveSellerId(undefined);
    onCloseAmazonTokenModal();
  };

  const onClickEasybillTokenBtn = () => {
    openEasybillTokenModal();
  };

  const getBilbeeDate = () => {
    try {
      const date = formatDate(
        new Date(localStorage.getItem('billbee')!),
        'dd.MM.yyyy'
      );
      return date;
    } catch (error) {
      return formatDate(new Date(), 'dd.MM.yyyy');
    }
  };

  const isEasybillConnected =
    easybillData && (easybillData.is_valid || easybillData.invalid_date);

  return (
    <>
      <div className='Integrations'>
        <div
          className='d-flex align-items-center Text--20 fw-bold pb-2 mb-2 border-bottom'
          data-testid='integrations-title'
        >
          <IntegrationsIcon className='me-2' />
          {t('Integrations')}
        </div>
        {integrations && (
          <div>
            <div
              className='d-flex align-items-center fw-bold mb-2'
              data-testid='integrations-amazon'
            >
              <AmazonIcon className='flex-shrink-0 me-2' />
              <span>Amazon</span>
            </div>
            {!!integrations.length ? (
              <>
                {integrations.map((i) => {
                  return (
                    <div
                      className='row gx-0 mb-2 border-top pt-2'
                      key={i.seller_id}
                    >
                      <div className='col align-self-center'>
                        <a
                          href={`https://www.amazon.de/sp?_encoding=UTF&language=en&seller=${i.seller_id}`}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='text-decoration-none Integrations__link d-inline-flex align-items-center'
                          data-testid={`amazon-link-${i.seller_id}`}
                        >
                          {i.seller_id}
                          <LinkIcon className='ms-1' />
                        </a>
                        {i.invalid && (
                          <div
                            className='text-danger Text--sm'
                            data-testid={`amazon-token-error-${i.seller_id}`}
                          >
                            {t('Token not valid')}
                          </div>
                        )}
                      </div>
                      <div className='col d-flex justify-content-end'>
                        {i.has_api_refresh_token && !i.invalid ? (
                          <span
                            data-testid={`amazon-integration-date-${i.seller_id}`}
                          >
                            {i.api_refresh_token_date
                              ? formatDate(
                                  normalizeDateFE(i.api_refresh_token_date),
                                  'dd.MM.yyyy'
                                )
                              : t('Yes')}
                          </span>
                        ) : (
                          <Button
                            styling='outline'
                            className='py-1 px-3'
                            onClick={() => {
                              setActiveSellerId(i.seller_id);
                              openAmazonTokenModal();
                            }}
                            data-testid={`amazon-token-btn-${i.seller_id}`}
                          >
                            {t('Update')}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <div className='Text--gray'>{t('No tokens connected')}</div>
            )}
            <div className='pt-2 mt-1'>
              <Button
                styling='naked-green'
                className='fw-600'
                onClick={openAmazonTokenModal}
                data-testid='amazon-integration-connect-btn'
              >
                +{t('Connect new token')}
              </Button>
            </div>
          </div>
        )}
        {vs3776BillbeeToken && (
          <>
            <div
              className='d-flex align-items-start justify-content-between fw-bold border-top pt-2 mt-2'
              data-testid='integrations-billbee'
            >
              <div className='d-flex align-items-center'>
                <img
                  src={BillbeeIcon}
                  alt='billbee'
                  style={{
                    width: 40,
                    height: 40,
                  }}
                  className='flex-shrink-0 me-2'
                />
                <span>Billbee</span>
              </div>
              {localStorage.getItem('billbee') && (
                <div className='pt-2'>
                  <TipIcon />
                </div>
              )}
            </div>
            {localStorage.getItem('billbee') ? (
              <div className='border-top pt-2 mt-2'>
                <div className='d-flex aling-items-center justify-content-between'>
                  <div className='fw-600'>{t('Connected')}</div>
                  <div>{getBilbeeDate()}</div>
                </div>
              </div>
            ) : (
              <Button
                styling='naked-green'
                className='fw-600 mt-3'
                onClick={openBillbeeTokenModal}
                data-testid='billbee-btn'
              >
                +{t('Connect')}
              </Button>
            )}
          </>
        )}
        {activeCompany?.EasybillClient && (
          <>
            <div
              className='d-flex align-items-start justify-content-between fw-bold border-top pt-2 mt-2'
              data-testid='integrations-easybill'
            >
              <div className='d-flex align-items-center'>
                <img
                  src={EasybillIcon}
                  alt='easybill'
                  style={{
                    width: 40,
                    height: 40,
                  }}
                  className='flex-shrink-0 me-2'
                />
                <span>easybill</span>
              </div>
              {isEasybillConnected && (
                <div className='pt-2'>
                  {easybillData.is_valid ? (
                    <TipIcon data-testid='easybill-valid' />
                  ) : (
                    <ErrorIcon data-testid='easybill-invalid' />
                  )}
                </div>
              )}
            </div>
            {isEasybillConnected && (
              <div
                className='border-top pt-2 mt-2'
                data-testid='easybill-connected'
              >
                <div className='d-flex aling-items-center justify-content-between'>
                  <div className='fw-600'>{t('Connected')}</div>
                  {easybillData.create_date && (
                    <div data-testid='easybill-connected-date'>
                      {formatDate(
                        normalizeDateFE(easybillData.create_date),
                        'dd.MM.yyyy'
                      )}
                    </div>
                  )}
                </div>
                {!easybillData.is_valid && easybillData.invalid_date && (
                  <div
                    className='Text--red Text--sm'
                    data-testid='easybill-invalid-date'
                  >
                    {t('Token not valid since *date*').replace(
                      '*date*',
                      formatDate(
                        normalizeDateFE(easybillData.invalid_date),
                        'dd.MM.yyyy'
                      )
                    )}
                  </div>
                )}
              </div>
            )}
            {(!easybillData || !easybillData.is_valid) && (
              <Button
                styling='naked-green'
                className='fw-600 mt-3'
                onClick={onClickEasybillTokenBtn}
                data-testid='easybill-btn'
              >
                {isEasybillConnected ? t('Update token') : `+${t('Connect')}`}
              </Button>
            )}
          </>
        )}
      </div>
      <AmazonTokenModal
        closeModal={closeAmazonTokenModal}
        isModalOpened={isAmazonTokenModalOpened}
        sellerId={activeSellerId}
      />
      <EasybillTokenModal
        closeModal={closeEasybillTokenModal}
        isModalOpened={isEasybillTokenModalOpened}
        initialIsTokenValid={isEasybillConnected ? easybillData.is_valid : true}
      />
      <BillbeeTokenModal
        closeModal={closeBillbeeTokenModal}
        isModalOpened={isBillbeeTokenModalOpened}
        onSubmit={() => {
          localStorage.setItem('billbee', new Date().toISOString());
          closeBillbeeTokenModal();
          dispatch(
            addNotification({
              type: 'success',
              text: t('Billbee connected'),
            })
          );
        }}
      />
    </>
  );
}
